import { ThemeOptions } from "@mui/material/styles";

import { Brand } from "merchant-portal-frontend/src/config";
import rbcLoginBackgroundImage from "merchant-portal-frontend/src/images/login/rbcLoginBackgroundImage.png";

import { RBCBranding } from "./branding.config";
import {
	black,
	rbcAppleGreen,
	rbcBrightBlueTint2,
	rbcBrightBlueTint4,
	rbcDarkGray,
	rbcError,
	rbcErrorDark,
	rbcErrorLight,
	rbcInfo,
	rbcInfoDark,
	rbcInfoLight,
	rbcLightGray,
	rbcPrimary,
	rbcPrimaryDark,
	rbcPrimaryLight,
	rbcPrimaryVeryLight,
	rbcSecondary,
	rbcSecondaryDark,
	rbcSecondaryLight,
	rbcSecondaryVeryLight,
	rbcSlate,
	rbcSuccess,
	rbcSuccessDark,
	rbcSuccessLight,
	rbcVeryLightGrey,
	rbcWarning,
	rbcWarningDark,
	rbcWarningLight,
	shadowColor,
	white,
} from "./colors";

const rbcTheme: ThemeOptions = {
	themeBrand: Brand.Rbc,
	loginBackgroundImage: rbcLoginBackgroundImage,
	primary1: rbcPrimary,
	primary2: rbcDarkGray,
	secondary1: rbcPrimaryLight,
	secondary2: "#1DE4BD",
	secondary3: "#A20DA9",
	tertiary2: "#49A9FC",
	white,
	black,
	layout: {
		gray: "#8691A0",
		blueGray: "#CFD8DC",
		extralightGray: "#EDEEEF",
	},
	text: {
		bodyAndIcons: rbcDarkGray,
		activeText: rbcPrimary,
		hoverText: rbcPrimary,
		disabledText: "#DADADA",
		hintText: "#8691A0",
		leftNavIconAndText: rbcSlate,
		logoText: rbcPrimary,
	},
	structure: {
		hoverBG: rbcPrimaryVeryLight,
		accent: "#B8DEFF",
		calendarLeftBG: "#E7F3FF",
		skeleton: rbcVeryLightGrey,
		cardStroke: "#FFC72C",
		shadow: `drop-shadow(0px 2px 3px ${shadowColor})`,
	},
	formElements: {
		formElementBorder: rbcLightGray,
		dropdownOpenBG: "#FCFCFF",
		calendarSelectionBG: rbcPrimary,
		requirementIconOutline: "#8691A0",
		requirementIconBGValid: "#038179",
		dropdownIconFill: rbcSlate,
	},
	buttons: {
		loginBase: "#95CEFF",
		loginText: rbcDarkGray,
		base: rbcPrimary,
		hover: "#3388CF",
		focus: "#0159A3",
		dangerHover: "#F25044",
		dangerFocus: "#AF1C11",
	},
	kpis: {
		gradients: {
			pending: "linear-gradient(97.23deg, #A800AF 0%, #4617CB 67.51%, #0008C3 100%)",
			authorized: `linear-gradient(96.86deg, #0008C3 1.32%, ${rbcPrimary} 44.06%, #0A89F4 71.12%, ${rbcPrimaryLight} 94.14%)`,
			settled: "linear-gradient(96.31deg, #2C9BFA 0.58%, #00C3BB 29.95%, #05B154 96.78%)",
			negative:
				"linear-gradient(286.53deg, #EF2291 10.78%, #DF1A48 44.9%, #EF4033 72.78%, #FF852C 98.91%)",
		},
		text: {
			pillText: rbcDarkGray,
			defaultText: "#001527",
		},
	},
	transactions: {
		authorizedBG: "#72BEFF",
		pendingBG: "#D583D8",
		settledBG: "#83E2AF",
		cancelledBG: "rgba(140, 144, 228, 0.8)",
		refundedBG: "#E58DBC",
		expiredBG: "#CFD8DC",
		chargedbackBG: "#FF6E63",
		text: {
			authorized: rbcPrimary,
			pending: "#A800AF",
			settled: "#008C40",
			cancelled: "#7F83E1",
			refunded: "#CF3287",
			expired: rbcSlate,
		},
	},
	icons: {
		primary: rbcPrimary,
		secondary: "#60F2A4",
		outline: rbcDarkGray,
	},
	...RBCBranding,
	palette: {
		primary: {
			main: rbcPrimary,
			light: rbcPrimaryLight,
			dark: rbcPrimaryDark,
			contrastText: white,
			veryLight: rbcPrimaryVeryLight,
		},
		secondary: {
			main: rbcSecondary,
			light: rbcSecondaryLight,
			dark: rbcSecondaryDark,
			contrastText: white,
		},
		error: {
			main: rbcError,
			light: rbcErrorLight,
			dark: rbcErrorDark,
			contrastText: white,
		},
		warning: {
			main: rbcWarning,
			light: rbcWarningLight,
			dark: rbcWarningDark,
			veryLight: rbcSecondaryVeryLight,
			contrastText: rbcDarkGray,
		},
		info: {
			main: rbcInfo,
			light: rbcInfoLight,
			dark: rbcInfoDark,
			contrastText: rbcDarkGray,
		},
		success: {
			main: rbcSuccess,
			light: rbcSuccessLight,
			dark: rbcSuccessDark,
			contrastText: rbcDarkGray,
		},
		common: {
			lineGray: rbcLightGray,
			darkGray: rbcDarkGray,
			gray: rbcSlate,
			black,
			white,
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					"minWidth": 0,
					"&.Mui-focusVisible": {
						border: `2px solid ${rbcAppleGreen}`,
					},
				},
				outlined: {
					"border": `1px solid ${rbcPrimary}`,
					"transition": "none",
					":hover": {
						transition: "none",
						border: `2px solid ${rbcPrimary}`,
						padding: "1.1rem 3.9rem",
					},
					":active": {
						border: `2px solid ${rbcDarkGray}`,
						color: `${rbcDarkGray}`,
					},
					"&.Mui-focusVisible": {
						border: `2px solid ${rbcAppleGreen}`,
					},
				},
				contained: {
					":hover": {
						backgroundColor: rbcPrimaryLight,
					},
					":active": {
						backgroundColor: rbcDarkGray,
					},
					":disabled": {
						backgroundColor: "#E2DDD7",
						color: white,
					},
					"&.Mui-focusVisible": {
						border: `2px solid ${rbcAppleGreen}`,
					},
				},
				text: {
					"borderBottom": `1px solid ${rbcPrimaryLight}`,
					":disabled": {
						color: "#E2DDD7",
						borderBottom: `2px solid #E2DDD7`,
					},
					":active": {
						color: rbcDarkGray,
					},
					":hover": {
						borderBottom: `2px solid ${rbcPrimary}`,
					},
					"&.Mui-focusVisible": {
						border: `2px solid ${rbcAppleGreen}`,
					},
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					color: rbcDarkGray,
				},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					color: rbcDarkGray,
					backgroundColor: white,
					border: `1px solid ${rbcLightGray}`,
				},
				arrow: {
					color: rbcLightGray,
				},
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: {
					"color": rbcPrimary,
					"&.Mui-disabled": {
						border: `1px solid #DADADA`,
						color: "#DADADA",
					},
					":hover": {
						backgroundColor: rbcBrightBlueTint4,
					},
					":active": {
						backgroundColor: rbcBrightBlueTint2,
					},
				},
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					"color": rbcSlate,
					"&.Mui-focused": {
						color: rbcSlate,
					},
				},
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				label: {
					"color": rbcDarkGray,
					"&.Mui-disabled": {
						color: "#DADADA",
					},
				},
			},
		},
		MuiDataGrid: {
			styleOverrides: {
				root: {
					color: rbcDarkGray,
				},
				sortIcon: {
					fill: rbcDarkGray,
				},
				menuIconButton: {
					fill: rbcDarkGray,
				},
				columnHeaders: {
					backgroundColor: rbcVeryLightGrey,
				},
			},
		},
		MuiSkeleton: {
			styleOverrides: {
				root: {
					backgroundColor: rbcVeryLightGrey,
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-notchedOutline": {
						border: `1px solid ${rbcLightGray}`,
					},
					"&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline": {
						border: `1px solid ${black}`,
					},
				},
			},
		},
		MuiAppBar: {
			styleOverrides: {
				root: {
					borderBottom: `1px solid ${rbcLightGray}`,
					color: rbcDarkGray,
				},
			},
		},
		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					"& .MuiAccordionSummary-expandIconWrapper": {
						"color": rbcPrimary,
						"&.Mui-disabled": {
							border: `2px solid #DADADA`,
							color: rbcPrimary,
						},
						":hover": {
							backgroundColor: rbcBrightBlueTint4,
						},
						":active": {
							backgroundColor: rbcBrightBlueTint2,
						},
					},
				},
			},
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					backgroundColor: rbcVeryLightGrey,
					fontWeight: 500,
					top: 0,
					zIndex: 1,
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					"& td:first-of-type": {
						borderLeftWidth: 1,
						borderLeftColor: rbcLightGray,
						borderLeftStyle: "solid",
					},
					"& td:last-of-type": {
						borderRightWidth: 1,
						borderRightColor: rbcLightGray,
						borderRightStyle: "solid",
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				body: {
					borderBottomWidth: 1,
					borderBottomColor: rbcLightGray,
					borderBottomStyle: "solid",
				},
				head: {
					"borderTopWidth": 1,
					"borderTopColor": rbcLightGray,
					"borderTopStyle": "solid",
					"borderBottomWidth": 1,
					"borderBottomColor": rbcLightGray,
					"borderBottomStyle": "solid",
					"&:first-of-type": {
						borderLeftWidth: 1,
						borderLeftColor: rbcLightGray,
						borderLeftStyle: "solid",
					},
					"&:last-of-type": {
						borderRightWidth: 1,
						borderRightColor: rbcLightGray,
						borderRightStyle: "solid",
					},
				},
			},
		},
		MuiToggleButtonGroup: {
			styleOverrides: {
				root: {
					"backgroundColor": rbcPrimaryVeryLight,
					"&.Mui-disabled": {
						backgroundColor: white,
						color: rbcLightGray,
					},
				},
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: {
					"color": rbcSlate,
					"&:hover": {
						backgroundColor: rbcPrimaryVeryLight,
						color: rbcPrimary,
					},
					"&.Mui-selected": {
						"backgroundColor": rbcPrimary,
						"color": white,
						"&:hover": {
							backgroundColor: rbcPrimary,
							color: white,
						},
					},
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					"&:hover": {
						backgroundColor: rbcPrimaryVeryLight,
						borderBottom: `3px solid ${rbcPrimaryLight}`,
						color: rbcPrimary,
					},
					"&:active": {
						backgroundColor: rbcPrimaryVeryLight,
						borderBottom: `3px solid ${rbcPrimary}`,
						color: rbcPrimary,
					},
				},
			},
		},
	},
};

export { rbcTheme };
